import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

import {
  useGetTasksByUser,
  useAddTask,
  useEditTask,
  useDeleteTask,
} from "../../../../api/addEmployee/employee";

import TaskHeader from "../TaskHeader";
import TaskCalendar from "../TaskCalendar";
import TaskList from "../TaskList";
import TaskLegend from "../TaskLegend";
import TaskModel from "../TaskModel";
import { notifyError, notifySuccess } from "../../../../utils/ToastifyProvider";

const EmployeeTask = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Decode token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUserId(decoded.userId || decoded.id);
      } catch (error) {
        // console.error("Invalid token", error);
      }
    }
  }, []);

  // API hooks - Fetch month-wise data for calendar
  const { data: userTasksData, isLoading: userTasksLoading } =
    useGetTasksByUser({
      userId: currentUserId,
      page: 1,
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
      enabled: !!currentUserId,
    });

  const userTasks = userTasksData?.tasks || [];

  const addTaskMutation = useAddTask();
  const editTaskMutation = useEditTask();
  const deleteTaskMutation = useDeleteTask();

  // ✅ Check if a date is in the past (before today)
  const isPastDate = (dateKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(dateKey);
    selectedDateObj.setHours(0, 0, 0, 0);
    return selectedDateObj < today;
  };

  // ✅ Check if a date is in the future (after today)
  const isFutureDate = (dateKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(dateKey);
    selectedDateObj.setHours(0, 0, 0, 0);
    return selectedDateObj > today;
  };

  // ✅ Check if a date is today
  const isToday = (dateKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(dateKey);
    selectedDateObj.setHours(0, 0, 0, 0);
    return selectedDateObj.getTime() === today.getTime();
  };

  const previousMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const nextMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const handleDateClick = (dateKey) => {
    // ✅ Prevent interaction with future dates
    if (isFutureDate(dateKey)) {
      notifyError("Cannot add tasks for future dates");
      return;
    }

    // ✅ Prevent interaction with past dates

    const existingTasks = userTasks.filter((t) => t.date?.startsWith(dateKey));
    setSelectedDate(dateKey);

    if (isPastDate(dateKey) && existingTasks.length === 0) {
      notifyError("Cannot add tasks for past dates");
      return;
    }

    // Auto-open modal only if:
    // 1. No tasks exist on this date AND
    // 2. The date is today
    if (existingTasks.length === 0 && isToday(dateKey)) {
      setTaskTitle("");
      setTaskDescription("");
      setIsEditMode(false);
      setEditingTask(null);
      setShowModal(true);
    }
  };

  const resetModal = () => {
    setTaskTitle("");
    setTaskDescription("");
    setShowModal(false);
    setIsEditMode(false);
    setEditingTask(null);
  };

  const handleTaskEdit = (task) => {
    const taskDate = task.date.split("T")[0];

    // ✅ Prevent editing tasks on future dates
    if (isFutureDate(taskDate)) {
      notifyError("Cannot edit tasks for future dates");
      return;
    }

    // ✅ Prevent editing tasks on past dates
    if (isPastDate(taskDate)) {
      notifyError("Cannot edit tasks for past dates");
      return;
    }

    // ✅ Only allow editing today's tasks
    if (!isToday(taskDate)) {
      notifyError("You can only edit today's tasks");
      return;
    }

    setIsEditMode(true);
    setEditingTask(task);
    setTaskTitle(task.title);
    setTaskDescription(task.description);
    setSelectedDate(taskDate);
    setShowModal(true);
  };

  const handleDeleteTask = (taskId, taskDate) => {
    // ✅ Prevent deleting tasks on future dates
    if (isFutureDate(taskDate)) {
      notifyError("Cannot delete tasks for future dates");
      return;
    }

    // ✅ Prevent deleting tasks on past dates
    if (isPastDate(taskDate)) {
      notifyError("Cannot delete tasks for past dates");
      return;
    }

    if (window.confirm("Are you sure you want to delete this task?")) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const handleTaskSubmit = async () => {
    if (!taskTitle.trim() || !taskDescription.trim()) {
      notifyError("Please fill in both title and description");
      return;
    }
    if (!selectedDate) {
      notifyError("Please select a date");
      return;
    }

    // ✅ Prevent submission for future dates
    if (isFutureDate(selectedDate)) {
      notifyError("Cannot add/edit tasks for future dates");
      return;
    }

    // ✅ Prevent submission for past dates
    if (isPastDate(selectedDate)) {
      notifyError("Cannot add/edit tasks for past dates");
      return;
    }

    // ✅ Only allow submission for today
    if (!isToday(selectedDate)) {
      notifyError("You can only add/edit tasks for today");
      return;
    }

    try {
      const taskData = {
        title: taskTitle,
        description: taskDescription,
        date: selectedDate,
      };

      if (isEditMode && editingTask) {
        await editTaskMutation.mutateAsync({
          taskId: editingTask._id,
          userId: currentUserId,
          ...taskData,
        });
        notifySuccess("Task updated successfully!");
      } else {
        await addTaskMutation.mutateAsync(taskData);
        // notifySuccess("Task added successfully!");
      }
      resetModal();
    } catch (error) {
      // console.error("Error saving task:", error);
      notifyError("Failed to save task. Please try again.");
    }
  };

  if (!currentUserId) {
    return (
      <div className="max-h-screen overflow-auto p-4">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden p-8 text-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-screen overflow-auto">
      <div className="w-[95%] my-5 max-w-[full] mx-auto bg-white rounded-2xl  overflow-hidden">
        <TaskHeader
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          previousMonth={previousMonth}
          nextMonth={nextMonth}
        />

        {/* No TaskFilters component for employees */}

        <TaskCalendar
          role="employee"
          currentDate={currentDate}
          userTasks={userTasks}
          allTasks={[]}
          selectedEmployee={null}
          selectedDate={selectedDate}
          onDateClick={handleDateClick}
        />

        {/* ✅ Only show task list for today's date or dates with existing tasks */}
        {selectedDate &&
          (isToday(selectedDate) ||
            userTasks.some((t) => t.date?.startsWith(selectedDate))) && (
            <TaskList
              title={`Tasks on ${new Date(selectedDate).toLocaleDateString(
                "en-GB",
                {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }
              )}`}
              tasks={userTasks.filter((t) => t.date?.startsWith(selectedDate))}
              isLoading={userTasksLoading}
              role="employee"
              users={[]}
              currentUserId={currentUserId}
              handleTaskEdit={handleTaskEdit}
              handleDeleteTask={handleDeleteTask}
              deleteTaskMutation={deleteTaskMutation}
            />
          )}

        <TaskLegend role="employee" />

        <TaskModel
          showModal={showModal}
          setShowModal={setShowModal}
          taskTitle={taskTitle}
          setTaskTitle={setTaskTitle}
          taskDescription={taskDescription}
          setTaskDescription={setTaskDescription}
          handleSubmit={handleTaskSubmit}
          isSubmitting={addTaskMutation.isPending || editTaskMutation.isPending}
          isEditMode={isEditMode}
          selectedEmployee={null}
          role="employee"
          selectedDate={selectedDate}
        />
      </div>
    </div>
  );
};

export default EmployeeTask;
